import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2, FileText, Download, Trash2, ExternalLink, User, Calendar, DollarSign
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABELS = {
  draft: { label: 'Rascunho', color: 'bg-stone-600 text-stone-200' },
  sent: { label: 'Enviado', color: 'bg-blue-500/20 text-blue-400' },
  accepted: { label: 'Aceito', color: 'bg-green-500/20 text-green-400' },
  rejected: { label: 'Recusado', color: 'bg-red-500/20 text-red-400' }
};

const formatBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch { return ''; }
};

const QuotesTab = ({ showMessage }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quotes`);
      const sorted = (res.data || []).slice().sort((a, b) => (b.number || 0) - (a.number || 0));
      setQuotes(sorted);
    } catch (e) {
      console.error(e);
      showMessage?.('Erro ao carregar orçamentos', 'error');
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  useEffect(() => { loadQuotes(); }, [loadQuotes]);

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este orçamento?')) return;
    try {
      await axios.delete(`${API}/quotes/${id}`);
      setQuotes(prev => prev.filter(q => q.id !== id));
      showMessage?.('Orçamento excluído');
    } catch (e) {
      console.error(e);
      showMessage?.('Erro ao excluir', 'error');
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      const res = await axios.put(`${API}/quotes/${id}`, { status });
      setQuotes(prev => prev.map(q => q.id === id ? res.data : q));
      showMessage?.('Status atualizado');
    } catch (e) {
      console.error(e);
      showMessage?.('Erro ao atualizar', 'error');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-amber-500 animate-spin" /></div>;
  }

  // KPIs
  const totalValue = quotes.reduce((acc, q) => acc + Number(q.total || 0), 0);
  const accepted = quotes.filter(q => q.status === 'accepted');
  const acceptedValue = accepted.reduce((acc, q) => acc + Number(q.total || 0), 0);
  const sent = quotes.filter(q => q.status === 'sent').length;

  return (
    <div className="space-y-6" data-testid="quotes-tab">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white">Orçamentos</h2>
          <p className="text-stone-400 text-sm">{quotes.length} orçamento(s) gerados • Para criar um novo, abra o lead no CRM</p>
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-center">
            <div className="text-stone-400">Total emitido</div>
            <div className="text-white font-bold">{formatBRL(totalValue)}</div>
          </div>
          <div className="bg-stone-800 border border-green-500/30 rounded-lg px-3 py-2 text-center">
            <div className="text-stone-400">Aceitos</div>
            <div className="text-green-400 font-bold">{formatBRL(acceptedValue)}</div>
          </div>
          <div className="bg-stone-800 border border-blue-500/30 rounded-lg px-3 py-2 text-center">
            <div className="text-stone-400">Enviados</div>
            <div className="text-blue-400 font-bold">{sent}</div>
          </div>
        </div>
      </div>

      {quotes.length === 0 ? (
        <div className="text-center py-16 bg-stone-800 rounded-xl border border-stone-700">
          <FileText className="w-12 h-12 text-stone-600 mx-auto mb-3" />
          <p className="text-stone-300 font-medium">Nenhum orçamento ainda</p>
          <p className="text-stone-500 text-sm">Vá no CRM, abra um lead e clique em "Gerar Orçamento".</p>
        </div>
      ) : (
        <div className="bg-stone-800 border border-stone-700 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-900 text-stone-400 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3">#</th>
                  <th className="text-left px-4 py-3">Cliente</th>
                  <th className="text-left px-4 py-3 hidden sm:table-cell">Data</th>
                  <th className="text-right px-4 py-3">Total</th>
                  <th className="text-center px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-700">
                {quotes.map(q => (
                  <tr key={q.id} data-testid={`quote-row-${q.id}`} className="hover:bg-stone-900/50">
                    <td className="px-4 py-3 text-amber-400 font-bold">#{String(q.number).padStart(4, '0')}</td>
                    <td className="px-4 py-3">
                      <div className="text-white font-medium">{q.leadName}</div>
                      <div className="text-stone-500 text-xs">{q.leadCity}{q.leadState ? `/${q.leadState}` : ''}</div>
                    </td>
                    <td className="px-4 py-3 text-stone-400 hidden sm:table-cell">{formatDate(q.createdAt)}</td>
                    <td className="px-4 py-3 text-right text-white font-bold">{formatBRL(q.total)}</td>
                    <td className="px-4 py-3 text-center">
                      <select
                        data-testid={`quote-status-${q.id}`}
                        value={q.status}
                        onChange={(e) => handleStatusChange(q.id, e.target.value)}
                        className={`text-xs px-2 py-1 rounded-full border-0 focus:outline-none cursor-pointer font-semibold ${STATUS_LABELS[q.status]?.color || 'bg-stone-700'}`}
                      >
                        {Object.entries(STATUS_LABELS).map(([k, v]) => (
                          <option key={k} value={k} className="bg-stone-800 text-white">{v.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 justify-end">
                        <a
                          href={`${API}/quotes/${q.id}/pdf`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`download-quote-${q.id}`}
                          className="p-2 text-amber-400 hover:bg-amber-500/20 rounded-lg"
                          title="Ver PDF"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                        <button
                          onClick={() => handleDelete(q.id)}
                          className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotesTab;
